/**
 * LanguageContext.js — Global language provider for Mobile Warehouse App
 *
 * Supported languages:
 *   "en"    — English (default)
 *   "zh"    — 简体中文 (Simplified Chinese)
 *   "es"    — Español (México)
 */
import React, { createContext, useContext, useState } from "react";

const TRANSLATIONS = {
  /* ════════════════════════════════════════════════════════════
     ENGLISH
  ════════════════════════════════════════════════════════════ */
  en: {
    /* ── Hub ── */
    warehouse_mobile:       "Warehouse Mobile",
    operations:             "Operations",
    location_section:       "Location",
    picking:                "Picking",
    quick_pull:             "Quick Pull",
    outbound:               "Outbound",
    picking_history:        "Picking History",
    find_cabinet:           "Check Stock",
    move_cabinet:           "Move Cabinet",
    stock_adjustment:       "Stock Adjustment",
    return_stock:           "Return Stock",
    sign_out:               "Sign out",
    pkb_footer:             "PKB WMS · Mobile Access",

    /* ── Login ── */
    sign_in:                "Sign In",
    use_wms_credentials:    "Use your WMS account credentials",
    username:               "Username",
    enter_username:         "Enter username",
    password:               "Password",
    enter_password:         "Enter password",
    signing_in:             "Signing in…",
    login_required:         "Username and password are required.",
    login_failed:           "Login failed.",
    network_error:          "Network error — please check your connection.",

    /* ── Shared ── */
    back:                   "Back",
    back_to_hub:            "Back to Hub",
    loading:                "Loading…",
    retry:                  "Retry",
    search:                 "Search",
    confirm:                "Confirm",
    cancel:                 "Cancel",
    save:                   "Save",
    add:                    "Add",
    go:                     "Go",
    today:                  "TODAY",
    history:                "History",
    refresh:                "Refresh",
    scan:                   "Scan",
    clear:                  "Clear",
    units:                  "units",
    open_camera:            "Scan Shelf",
    or_enter_manually:      "Or enter barcode manually",
    type_instead:           "⌨ Type Instead (Light / Broken Barcode)",
    hide_manual:            "Hide Manual Input",
    looking_up:             "Looking up…",
    try_again:              "Try Again",
    scan_another:           "Scan Another",
    redo:                   "✎ Redo",

    /* ── Outbound ── */
    outbound_title:         "Outbound",
    outbound_sub:           "Scan invoice to start",
    scan_invoice:           "Scan Invoice",
    enter_invoice:          "Enter Invoice",
    camera_scan:            "Camera Scan",
    scan_invoice_barcode:   "Scan invoice barcode",
    invoice_not_found:      "Invoice not found.",
    invoice_placeholder2:   "e.g. INV-2024-001",

    /* ── Check Stock (LocationScanView) ── */
    find_cabinet_title:     "Check Stock",
    find_cabinet_sub:       "Scan cabinet or shelf barcode to see locations",
    cabinet_barcode_hint:   "Cabinet barcode (e.g. 8001-B15) or shelf barcode (e.g. A-01)",
    barcode_placeholder:    "8001-B15 or A-01",
    looking_up_shelf:       "Looking up…",
    lookup_failed:          "Lookup failed. Please try again.",
    cabinet_label:          "Cabinet",
    shelf_label:            "Shelf Location",
    no_locations_found:     "No shelf locations found for this cabinet.",
    found_in_locations:     "Found in",
    location_s:             "location(s)",
    shelf_empty:            "This shelf is empty.",
    cabinet_types_on_shelf: "cabinet type(s) on this shelf",

    /* ── Move Cabinet (MoveStockView) ── */
    move_cabinet_title:     "Move Cabinet",
    move_cabinet_sub:       "FROM shelf → select items → TO shelf",
    step_from_shelf:        "FROM Shelf",
    step_select_items:      "Select Items",
    step_to_shelf:          "TO Shelf",
    step_confirm_move:      "Confirm",
    move_complete:          "Move Complete",
    move_more:              "Move More",
    step1_scan_from:        "Step 1: Scan FROM shelf barcode",
    step3_scan_to:          "Step 3: Scan TO shelf barcode",
    loading_shelf:          "Loading shelf…",
    checking:               "Checking…",
    shelf_not_found:        "Shelf not found. Scan a shelf label (e.g. A-01).",
    no_stock_on_shelf:      "No stock found at this shelf.",
    could_not_read_shelf:   "Could not read shelf. Please try again.",
    from_to_same:           "FROM and TO shelf cannot be the same.",
    shelf_not_found_to:     "Shelf not found. Scan a shelf label (e.g. B-02).",
    item_types_at_shelf:    "item type(s) at this shelf",
    deselect_all:           "Deselect All",
    select_all:             "Select All",
    available:              "available",
    max:                    "max",
    select_at_least_one:    "Select at least one item to move.",
    no_valid_items:         "No valid items to move.",
    next_scan_to:           "Next → Scan TO Shelf",
    selected:               "selected",
    items_selected_to_move: "item type(s) selected to move",
    items_to_move:          "Items to Move",
    confirm_move:           "Confirm Move",
    confirm_move_title:     "Confirm this move?",
    move_types_label:            "types",
    moving:                 "Moving…",
    move_failed:            "Move failed. Please try again.",
    item_types_moved:       "item type(s) moved",
    change_to_shelf:        "← Change TO Shelf",
    back_to_selection:      "← Back to Item Selection",
    /* multi-location split */
    placed_so_far:          "Placed so far:",
    placed_at:              "Placed at:",
    from_location:          "From:",
    qty_max:                "max",
    remaining_next:         "remaining → scan next location",
    place_here_partial:     "Place here → scan next location",
    qty_left:               "left",
    /* step 3 review panel */
    review_hide:            "▲ Hide",
    review_edit_qty:        "▼ Review / Edit Qty",
    /* sku mode strings */
    sku_mode_shelf_err:     "This looks like a shelf barcode. Please scan a SKU barcode.",
    sku_mode_placeholder:   "Scan or type SKU barcode (e.g. 8001-B15)",
    sku_mode_change:        "Change SKU",
    sku_mode_select_from:   "Select FROM shelf",
    sku_mode_current_stock: "Current stock:",
    sku_mode_searching:     "Looking up SKU locations…",
    sku_mode_not_found:     "No shelf locations found for this SKU.",
    /* -D suffix auto-match */
    d_trying_alt:           "Trying alternate SKU…",
    d_matched_with:         "Matched as",
    /* move cabinet step 0 card labels */
    mv_card_shelf_title:    "Scan Shelf Location",
    mv_card_shelf_sub:      "Scan a shelf barcode to see all SKUs on it",
    mv_card_sku_title:      "Scan SKU Barcode",
    mv_card_sku_sub:        "Scan a product barcode to find which shelves hold it",
    mv_from_shelf_placeholder: "FROM shelf (e.g. A-01)",

    /* ── Stock Adjustment ── */
    stock_adj_title:        "Stock Adjustment",
    stock_adj_sub:          "Scan shelf · view stock · adjust quantities",
    card_shelf_title:        "Scan Shelf",
    card_shelf_sub:         "Scan a shelf barcode to view and adjust all inventory on that shelf",
    card_sku_title:         "Scan Cabinet",
    card_sku_sub:           "Scan a cabinet barcode to find all shelf locations carrying that cabinet",
    step1_scan_shelf:       "Step 1: Scan shelf barcode (e.g. A-01)",
    open_camera_scan_shelf: "Open Camera to Scan Shelf",
    looking_up_shelf2:      "Looking up shelf...",
    shelf_not_found2:       "Shelf not found. Scan a shelf label (e.g. A-01).",
    could_not_read:         "Could not read shelf. Please try again.",
    loading_shelf_inv:      "Loading shelf inventory...",
    no_stock_on_shelf2:     "No stock currently on this shelf — add SKUs below.",
    was:                    "Was:",
    add_new_sku:            "Add New SKU to This Shelf",
    scan_sku_barcode:       "Scan SKU Barcode",
    or_enter_sku:           "Or enter SKU manually",
    shelf_barcode_reject:   "That's a shelf barcode — scan a cabinet/SKU barcode instead.",
    sku_already_in_list:    "is already in the list.",
    no_changes:             "No changes yet",
    submit_changes:         "Submit",
    change_s:               "Change(s)",
    saving:                 "Saving...",
    no_changes_to_submit:   "No changes to submit — adjust at least one quantity.",
    adjustment_saved:       "Adjustment Saved",
    skus_updated:           "SKU(s) updated",
    adjust_another:         "Adjust Another Shelf",
    adj_failed:             "Failed to submit adjustments.",
    color_code_label:       "Color Code",
    color_placeholder:      "Color Code e.g. B12",
    color_variants:         "color variant(s) — select or type one:",
    new_sku_enter_color:    "New SKU — enter the Color Code for this item:",
    empty_barcode:          "Empty barcode.",

    /* ── Return Stock ── */
    return_stock_title:     "Return Stock",
    return_stock_sub:       "Return items to shelf",
    /* step bar */
    ret_step_cabinet:       "Cabinet",
    ret_step_location_qty:  "Location & Qty",
    ret_step_details:       "Details",
    ret_step_confirm:       "Confirm",
    /* step 0 — scan SKU */
    ret_scan_sku_prompt:    "Scan or enter cabinet SKU barcode",
    ret_scan_sku_hint:      "e.g. 8001-B15 or GREY-W3060",
    ret_scan_sku_placeholder: "Scan or type SKU barcode",
    ret_looking_up_sku:     "Looking up SKU…",
    ret_shelf_barcode_err:  "That looks like a shelf barcode — please scan a cabinet SKU.",
    ret_sku_lookup_fail:    "Could not look up SKU. Please try again.",
    /* step 1 — pick location */
    ret_sku_badge:          "Cabinet SKU",
    ret_return_to_section:  "Return to location",
    ret_no_existing_loc:    "No existing shelf location for this SKU — please scan a shelf below.",
    ret_current_stock:      "Current stock:",
    ret_choose_another:     "Choose another shelf",
    ret_scan_diff_shelf:    "Scan a different shelf barcode",
    ret_shelf_not_found:    "Shelf not found. Please scan a valid shelf barcode.",
    ret_shelf_lookup_fail:  "Could not read shelf. Please try again.",
    ret_qty_to_return:      "Quantity to return →",
    ret_next_details:       "Next — Add Details →",
    /* step 2 — details */
    ret_return_to_badge:    "Return to",
    ret_invoice_label:      "Invoice",
    ret_optional:           "(optional)",
    ret_invoice_placeholder: "e.g. 274545-A",
    ret_note_label:         "Note",
    ret_note_placeholder:   "e.g. Customer return, wrong colour",
    ret_review_confirm:     "Review & Confirm →",
    ret_back:               "← Back",
    /* step 3 — confirm */
    ret_confirm_title:      "Confirm Return",
    ret_col_sku:            "SKU",
    ret_col_return_to:      "Return to",
    ret_col_quantity:       "Quantity",
    ret_col_invoice:        "Invoice",
    ret_col_note:           "Note",
    ret_confirm_btn:        "Confirm Return ✓",
    ret_returning:          "Returning…",
    /* errors */
    ret_no_shelf_err:       "No target shelf selected.",
    ret_qty_err:            "Quantity must be at least 1.",
    ret_resolve_err:        "Could not resolve shelf ID. Please re-select the location.",
    ret_failed:             "Return failed. Please try again.",
    /* done page */
    ret_done_title:         "Stock Returned",
    ret_done_sku:           "SKU",
    ret_done_location:      "Location",
    ret_done_qty:           "Qty",
    ret_done_invoice:       "Invoice",
    ret_done_note:          "Note",
    ret_return_another:     "Return Another",
    /* legacy keys kept for safety */
    step_shelf:             "Shelf",
    step_cabinet:           "Cabinet",
    step_confirm_return:    "Confirm",
    scan_shelf_label:       "Scan shelf barcode (e.g. A-01)",
    scan_cabinet_label:     "Scan cabinet barcode",
    or_enter_cab_manually:  "Or enter cabinet barcode manually",
    shelf_barcode_reject2:  "That's a shelf barcode. Scan the cabinet barcode instead.",
    valid_qty_error:        "Please enter a valid quantity.",
    qty_to_return:          "Quantity to Return",
    enter_quantity:         "Enter quantity",
    confirm_return:         "Confirm Return",
    returning:              "Returning...",
    return_failed:          "Failed to return stock. Please try again.",
    stock_returned:         "Stock Returned",
    return_more:            "Return More",

    /* ── Picking — ScanSession auto-save / exit confirm ── */
    pick_resume_title:      "Resume last session?",
    pick_resume_invoice:    "Invoice:",
    pick_resume_types:      "item type(s) scanned",
    pick_resume_units:      "units",
    pick_saved_min_ago:     "min ago",
    pick_saved_recently:    "Saved recently",
    pick_resume_btn:        "Resume",
    pick_discard_btn:       "Discard",
    pick_exit_title:        "Leave this session?",
    pick_exit_body:         "item type(s) scanned are auto-saved. You can resume next time you open Picking.",
    pick_exit_for:          "for",
    pick_leave_btn:         "Leave",
    pick_stay_btn:          "Stay",

    /* ── Picking — OrderList ── */
    assembly_picking:       "Assembly Picking",
    orders_needing_pick:    "Orders needing pick",
    no_orders_to_pick:      "No orders to pick",
    start_new_scan:         "Start New Scan Session",
    start_new_scan_sub:     "Scan cabinet barcodes without pre-selecting an order",
    overdue:                "OVERDUE",
    items_picked:           "items picked",

    /* ── Picking — ScanSession ── */
    picking_free_scan:      "Picking — Free Scan",
    items_scanned:          "item type(s) scanned",
    invoice:                "Invoice:",
    invoice_placeholder:    "Enter invoice no. or scan INV-barcode",
    change:                 "Change",
    scan_cabinet_barcode:   "Scan Cabinet Barcode",
    enter_search_barcode:   "Enter or search barcode",
    use_camera:             "Use Camera",
    scan_list:              "Scan List",
    no_items_scanned:       "No items scanned yet",
    clear_all:              "Clear All",
    pull_order:             "Pull Order",
    total_units:            "total unit(s)",
    types:                  "type(s)",
    total_types:            "type(s)",
    invoice_set:            "Invoice set:",
    added:                  "Added:",
    qty_updated:            "Qty updated:",
    invoice_required:        "Invoice number required before submitting.",
    empty_nothing:          "Scan list is empty. Nothing to pull.",
    failed_submit:          "Failed to submit. Please retry.",
    step_enter_invoice:     "Step 1 of 2 — Enter Invoice",
    enter_invoice_prompt:   "Enter Invoice Number",
    enter_invoice_hint:     "Scan the barcode or type it in below",
    start_scanning:         "Start Scanning",

    /* ── Picking — Done ── */
    pull_complete:          "Pull Order Complete",
    pull_submitted:         "Pull Order Submitted",
    start_new_scan_btn:     "Start New Scan",
    view_history:           "View History",
    recorded:               "recorded",

    /* ── Picking — History ── */
    picking_history_title:  "Picking History",
    records:                "record(s)",
    date_placeholder:       "Date YYYY-MM-DD",
    invoice_no:             "Invoice no.",
    no_records:             "No records found",
    failed_load_history:    "Failed to load history.",

    /* ── Edit modal ── */
    edit_scan_entry:        "Edit Scan Entry",
    barcode:                "Barcode:",
    item:                   "Item:",
    qty:                    "Qty:",
    note:                   "Note:",
    note_optional:          "Optional note",

    /* ── Pull Order confirm ── */
    confirm_pull_order:     "Confirm Pull Order",
    note_optional_label:    "Note (optional):",
    add_note:               "Add a note…",

    /* ── Quick Pull ── */
    quick_pull_title:       "Quick Pull",
    scan_any_cabinet:       "scan any cabinet",
    types_label:            "Types",
    cabinets_label:         "Cabinets",
    scan_list_empty:        "Scan List is empty",
    scan_start_hint:        "Scan any cabinet barcode to start",
    done_btn:               "Done",
    new_scan:               "New Scan",
    quick_pull_done:        "Quick Pull Done",
    quick_pull_recorded:    "Quick Pull Recorded",
    confirm_quick_pull:     "Confirm Quick Pull",
    confirm_submit:         "Confirm & Submit",
    no_invoice_needed:      "Scan cabinets above — no invoice needed",
    failed_try_again:       "Failed. Try again.",

    /* ── History page ── */
    hub:                    "← Hub",
    select_date:            "Select date",
    invoice_number:         "Invoice number",
    show_all:               "Show all",
    nothing_on:             "Nothing on",
    try_different:          "Try a different date or invoice",
    loading_history:        "Loading history…",
    total_records:          "record(s) total",
    failed_load_orders:     "Failed to load orders. Check connection.",

    /* ── Quick Pull note ── */
    scan_at_least_one:      "Scan at least one cabinet first.",
  },

  /* ════════════════════════════════════════════════════════════
     简体中文
  ════════════════════════════════════════════════════════════ */
  zh: {
    /* ── Hub ── */
    warehouse_mobile:       "仓库移动端",
    operations:             "操作",
    location_section:       "位置管理",
    picking:                "拣货",
    quick_pull:             "快速出库",
    outbound:               "出库",
    picking_history:        "拣货历史",
    find_cabinet:           "查看库存",
    move_cabinet:           "移动橱柜",
    stock_adjustment:       "库存调整",
    return_stock:           "退货入库",
    sign_out:               "退出登录",
    pkb_footer:             "PKB 仓库系统 · 移动端",

    /* ── Login ── */
    sign_in:                "登录",
    use_wms_credentials:    "使用您的仓库系统账号登录",
    username:               "用户名",
    enter_username:         "请输入用户名",
    password:               "密码",
    enter_password:         "请输入密码",
    signing_in:             "登录中…",
    login_required:         "用户名和密码不能为空。",
    login_failed:           "登录失败。",
    network_error:          "网络错误 — 请检查网络连接。",

    /* ── Shared ── */
    back:                   "返回",
    back_to_hub:            "返回主页",
    loading:                "加载中…",
    retry:                  "重试",
    search:                 "搜索",
    confirm:                "确认",
    cancel:                 "取消",
    save:                   "保存",
    add:                    "添加",
    go:                     "确定",
    today:                  "今天",
    history:                "历史",
    refresh:                "刷新",
    scan:                   "扫描",
    clear:                  "清除",
    units:                  "件",
    open_camera:            "扫描货架",
    or_enter_manually:      "或手动输入条码",
    type_instead:           "⌨ 手动输入（条码损坏时使用）",
    hide_manual:            "隐藏手动输入",
    looking_up:             "查询中…",
    try_again:              "重试",
    scan_another:           "继续扫描",
    redo:                   "✎ 重扫",

    /* ── Outbound ── */
    outbound_title:         "出库",
    outbound_sub:           "扫描订单号开始出库",
    scan_invoice:           "扫描订单",
    enter_invoice:          "输入订单号",
    camera_scan:            "摄像头扫描",
    scan_invoice_barcode:   "扫描订单条码",
    invoice_not_found:      "未找到订单。",
    invoice_placeholder2:   "如：INV-2024-001",

    /* ── Check Stock ── */
    find_cabinet_title:     "查看库存",
    find_cabinet_sub:       "扫描橱柜或货架条码查看位置",
    cabinet_barcode_hint:   "橱柜条码（如 8001-B15）或货架条码（如 A-01）",
    barcode_placeholder:    "8001-B15 或 A-01",
    looking_up_shelf:       "查询中…",
    lookup_failed:          "查询失败，请重试。",
    cabinet_label:          "橱柜",
    shelf_label:            "货架位置",
    no_locations_found:     "未找到该橱柜的货架位置。",
    found_in_locations:     "共在",
    location_s:             "个位置找到",
    shelf_empty:            "该货架暂无库存。",
    cabinet_types_on_shelf: "种橱柜在此货架",

    /* ── Move Cabinet ── */
    move_cabinet_title:     "移动橱柜",
    move_cabinet_sub:       "来源货架 → 选择产品 → 目标货架",
    step_from_shelf:        "来源货架",
    step_select_items:      "选择产品",
    step_to_shelf:          "目标货架",
    step_confirm_move:      "确认",
    move_complete:          "移动完成",
    move_more:              "继续移动",
    step1_scan_from:        "第1步：扫描来源货架条码",
    step3_scan_to:          "第3步：扫描目标货架条码",
    loading_shelf:          "加载货架中…",
    checking:               "检查中…",
    shelf_not_found:        "未找到货架，请扫描货架标签（如 A-01）。",
    no_stock_on_shelf:      "该货架暂无库存。",
    could_not_read_shelf:   "无法读取货架，请重试。",
    from_to_same:           "来源货架和目标货架不能相同。",
    shelf_not_found_to:     "未找到目标货架，请扫描货架标签（如 B-02）。",
    item_types_at_shelf:    "种产品在此货架",
    deselect_all:           "取消全选",
    select_all:             "全选",
    available:              "可用",
    max:                    "最多",
    select_at_least_one:    "请至少选择一种产品移动。",
    no_valid_items:         "没有可移动的产品。",
    next_scan_to:           "下一步 → 扫描目标货架",
    selected:               "已选",
    items_selected_to_move: "种产品待移动",
    items_to_move:          "待移动产品",
    confirm_move:           "确认移动",
    confirm_move_title:     "确认此次移动？",
    move_types_label:       "种类",
    moving:                 "移动中…",
    move_failed:            "移动失败，请重试。",
    item_types_moved:       "种产品已移动",
    change_to_shelf:        "← 更改目标货架",
    back_to_selection:      "← 返回选择产品",
    placed_so_far:          "已放置：",
    placed_at:              "放置位置：",
    from_location:          "来源：",
    qty_max:                "最多",
    remaining_next:         "件待放 → 扫描下一货架",
    place_here_partial:     "放置此处 → 扫描下一货架",
    qty_left:               "件待放",
    /* 第3步 review 面板 */
    review_hide:            "▲ 收起",
    review_edit_qty:        "▼ 查看 / 修改数量",
    /* SKU 模式 */
    sku_mode_shelf_err:     "这是货架条码，请扫描橱柜/SKU 条码。",
    sku_mode_placeholder:   "扫描或输入 SKU 条码（如 8001-B15）",
    sku_mode_change:        "更换 SKU",
    sku_mode_select_from:   "选择来源货架",
    sku_mode_current_stock: "当前库存：",
    sku_mode_searching:     "查询 SKU 所在货架中…",
    sku_mode_not_found:     "未找到该 SKU 的货架位置。",
    /* -D 后缀自动匹配 */
    d_trying_alt:           "正在尝试备用 SKU…",
    d_matched_with:         "已匹配为",
    /* 第0步卡片标签 */
    mv_card_shelf_title:    "扫描货架位置",
    mv_card_shelf_sub:      "扫描货架条码，查看该货架上的所有产品",
    mv_card_sku_title:      "扫描 SKU 条码",
    mv_card_sku_sub:        "扫描产品条码，找到存放该产品的所有货架",
    mv_from_shelf_placeholder: "来源货架（如 A-01）",

    /* ── Stock Adjustment ── */
    stock_adj_title:        "库存调整",
    stock_adj_sub:          "扫描货架 · 查看库存 · 调整数量",
    card_shelf_title:        "扫库位",
    card_shelf_sub:         "扫库位条码，查看并修改该库位上的所有库存",
    card_sku_title:         "扫柜子",
    card_sku_sub:           "扫柜子条码，找到存放该柜子的所有库位",
    step1_scan_shelf:       "第1步：扫描货架条码（如 A-01）",
    open_camera_scan_shelf: "打开摄像头扫描货架",
    looking_up_shelf2:      "查询货架中...",
    shelf_not_found2:       "未找到货架，请扫描货架标签（如 A-01）。",
    could_not_read:         "无法读取货架，请重试。",
    loading_shelf_inv:      "加载货架库存中...",
    no_stock_on_shelf2:     "该货架暂无库存 — 在下方添加 SKU。",
    was:                    "原数量：",
    add_new_sku:            "添加新 SKU 到此货架",
    scan_sku_barcode:       "扫描 SKU 条码",
    or_enter_sku:           "或手动输入 SKU",
    shelf_barcode_reject:   "这是货架条码 — 请扫描橱柜/SKU 条码。",
    sku_already_in_list:    "已在列表中。",
    no_changes:             "暂无变更",
    submit_changes:         "提交",
    change_s:               "项变更",
    saving:                 "保存中...",
    no_changes_to_submit:   "没有变更可提交 — 请先调整至少一个数量。",
    adjustment_saved:       "调整已保存",
    skus_updated:           "个 SKU 已更新",
    adjust_another:         "继续调整其他货架",
    adj_failed:             "调整提交失败。",
    color_code_label:       "颜色代码",
    color_placeholder:      "颜色代码，如 B12",
    color_variants:         "种颜色变体 — 请选择或输入：",
    new_sku_enter_color:    "新 SKU — 请输入该产品的颜色代码：",
    empty_barcode:          "条码为空。",

    /* ── Return Stock ── */
    return_stock_title:     "退货入库",
    return_stock_sub:       "将产品退回货架",
    /* step bar */
    ret_step_cabinet:       "橱柜",
    ret_step_location_qty:  "库位 & 数量",
    ret_step_details:       "详情",
    ret_step_confirm:       "确认",
    /* step 0 — scan SKU */
    ret_scan_sku_prompt:    "扫描或输入橱柜条码",
    ret_scan_sku_hint:      "如 8001-B15 或 GREY-W3060",
    ret_scan_sku_placeholder: "扫描或输入条码",
    ret_looking_up_sku:     "查询中…",
    ret_shelf_barcode_err:  "这是货架条码，请扫描橱柜 SKU 条码。",
    ret_sku_lookup_fail:    "无法查询该 SKU，请重试。",
    /* step 1 — pick location */
    ret_sku_badge:          "橱柜 SKU",
    ret_return_to_section:  "退回库位",
    ret_no_existing_loc:    "该 SKU 暂无库位记录，请扫描下方货架条码。",
    ret_current_stock:      "当前库存：",
    ret_choose_another:     "选择其他货架",
    ret_scan_diff_shelf:    "扫描其他货架条码",
    ret_shelf_not_found:    "未找到货架，请扫描有效的货架条码。",
    ret_shelf_lookup_fail:  "无法读取货架，请重试。",
    ret_qty_to_return:      "退回数量 →",
    ret_next_details:       "下一步 — 填写详情 →",
    /* step 2 — details */
    ret_return_to_badge:    "退回至",
    ret_invoice_label:      "发票号",
    ret_optional:           "（可选）",
    ret_invoice_placeholder: "如 274545-A",
    ret_note_label:         "备注",
    ret_note_placeholder:   "如 客户退货，颜色错误",
    ret_review_confirm:     "确认提交 →",
    ret_back:               "← 返回",
    /* step 3 — confirm */
    ret_confirm_title:      "确认退货",
    ret_col_sku:            "产品",
    ret_col_return_to:      "退回库位",
    ret_col_quantity:       "数量",
    ret_col_invoice:        "发票号",
    ret_col_note:           "备注",
    ret_confirm_btn:        "确认退货 ✓",
    ret_returning:          "退货中…",
    /* errors */
    ret_no_shelf_err:       "未选择目标库位。",
    ret_qty_err:            "数量必须至少为 1。",
    ret_resolve_err:        "无法解析库位 ID，请重新选择库位。",
    ret_failed:             "退货失败，请重试。",
    /* done page */
    ret_done_title:         "退货成功",
    ret_done_sku:           "产品",
    ret_done_location:      "库位",
    ret_done_qty:           "数量",
    ret_done_invoice:       "发票号",
    ret_done_note:          "备注",
    ret_return_another:     "继续退货",
    /* legacy keys kept for safety */
    step_shelf:             "货架",
    step_cabinet:           "橱柜",
    step_confirm_return:    "确认",
    scan_shelf_label:       "扫描货架条码（如 A-01）",
    scan_cabinet_label:     "扫描橱柜条码",
    or_enter_cab_manually:  "或手动输入橱柜条码",
    shelf_barcode_reject2:  "这是货架条码，请扫描橱柜条码。",
    valid_qty_error:        "请输入有效数量。",
    qty_to_return:          "退还数量",
    enter_quantity:         "输入数量",
    confirm_return:         "确认退货",
    returning:              "退货中...",
    return_failed:          "退货失败，请重试。",
    stock_returned:         "退货成功",
    return_more:            "继续退货",

    /* ── Picking — ScanSession auto-save / exit confirm ── */
    pick_resume_title:      "继续上次扫描？",
    pick_resume_invoice:    "订单号：",
    pick_resume_types:      "个品类已扫描",
    pick_resume_units:      "件",
    pick_saved_min_ago:     "分钟前保存",
    pick_saved_recently:    "刚刚保存",
    pick_resume_btn:        "继续",
    pick_discard_btn:       "丢弃",
    pick_exit_title:        "退出当前扫描？",
    pick_exit_body:         "个品类已自动保存，下次打开拣货时可恢复。",
    pick_exit_for:          "订单",
    pick_leave_btn:         "退出",
    pick_stay_btn:          "继续扫描",

    /* ── Picking — OrderList ── */
    assembly_picking:       "拣货任务",
    orders_needing_pick:    "待拣货订单",
    no_orders_to_pick:      "暂无待拣货订单",
    start_new_scan:         "开始新扫描会话",
    start_new_scan_sub:     "无需选择订单，直接扫描橱柜条码",
    overdue:                "已逾期",
    items_picked:           "件已拣",

    /* ── Picking — ScanSession ── */
    picking_free_scan:      "拣货 — 自由扫描",
    items_scanned:          "个品类已扫描",
    invoice:                "订单号：",
    invoice_placeholder:    "输入订单号或扫描 INV 条码",
    change:                 "修改",
    scan_cabinet_barcode:   "扫描橱柜条码",
    enter_search_barcode:   "输入或搜索条码",
    use_camera:             "使用摄像头",
    scan_list:              "扫描列表",
    no_items_scanned:       "还没有扫描任何条目",
    clear_all:              "清空全部",
    pull_order:             "提交出库",
    total_units:            "件",
    types:                  "个品类",
    total_types:            "个品类",
    invoice_set:            "订单号已设置：",
    added:                  "已添加：",
    qty_updated:            "数量已更新：",
    invoice_required:       "提交前必须填写订单号。",
    empty_nothing:          "扫描列表为空，无法提交。",
    failed_submit:          "提交失败，请重试。",
    step_enter_invoice:     "第 1 步 / 共 2 步 — 录入订单号",
    enter_invoice_prompt:   "请输入订单号",
    enter_invoice_hint:     "扫描条码或在下方手动输入",
    start_scanning:         "开始扫描",

    /* ── Picking — Done ── */
    pull_complete:          "出库完成",
    pull_submitted:         "出库已提交",
    start_new_scan_btn:     "开始新扫描",
    view_history:           "查看历史",
    recorded:               "已记录",

    /* ── Picking — History ── */
    picking_history_title:  "拣货历史",
    records:                "条记录",
    date_placeholder:       "日期 YYYY-MM-DD",
    invoice_no:             "订单号",
    no_records:             "未找到记录",
    failed_load_history:    "加载失败，请检查网络。",

    /* ── Edit modal ── */
    edit_scan_entry:        "编辑扫描条目",
    barcode:                "条码：",
    item:                   "产品：",
    qty:                    "数量：",
    note:                   "备注：",
    note_optional:          "可选备注",

    /* ── Pull Order confirm ── */
    confirm_pull_order:     "确认出库",
    note_optional_label:    "备注（可选）：",
    add_note:               "添加备注…",

    /* ── Quick Pull ── */
    quick_pull_title:       "快速出库",
    scan_any_cabinet:       "扫描任意橱柜",
    types_label:            "品类",
    cabinets_label:         "橱柜",
    scan_list_empty:        "扫描列表为空",
    scan_start_hint:        "扫描任意橱柜条码开始",
    done_btn:               "完成",
    new_scan:               "新扫描",
    quick_pull_done:        "快速出库完成",
    quick_pull_recorded:    "快速出库已记录",
    confirm_quick_pull:     "确认快速出库",
    confirm_submit:         "确认并提交",
    no_invoice_needed:      "在上方扫描橱柜 — 无需订单号",
    failed_try_again:       "操作失败，请重试。",

    /* ── History page ── */
    hub:                    "← 主页",
    select_date:            "选择日期",
    invoice_number:         "订单号",
    show_all:               "显示全部",
    nothing_on:             "该日期无记录：",
    try_different:          "请换个日期或订单号",
    loading_history:        "加载历史中…",
    total_records:          "条记录",
    failed_load_orders:     "加载失败，请检查网络。",
    scan_at_least_one:      "请先扫描至少一件橱柜。",
  },

  /* ════════════════════════════════════════════════════════════
     Español (México)
  ════════════════════════════════════════════════════════════ */
  es: {
    /* ── Hub ── */
    warehouse_mobile:       "Almacén Móvil",
    operations:             "Operaciones",
    location_section:       "Ubicación",
    picking:                "Surtido",
    quick_pull:             "Salida Rápida",
    outbound:               "Salida",
    picking_history:        "Historial de Surtido",
    find_cabinet:           "Ver Stock",
    move_cabinet:           "Mover Gabinete",
    stock_adjustment:       "Ajuste de Inventario",
    return_stock:           "Devolver Mercancía",
    sign_out:               "Cerrar Sesión",
    pkb_footer:             "PKB WMS · Acceso Móvil",

    /* ── Login ── */
    sign_in:                "Iniciar Sesión",
    use_wms_credentials:    "Use sus credenciales del sistema de almacén",
    username:               "Usuario",
    enter_username:         "Ingrese usuario",
    password:               "Contraseña",
    enter_password:         "Ingrese contraseña",
    signing_in:             "Iniciando sesión…",
    login_required:         "Usuario y contraseña son requeridos.",
    login_failed:           "Inicio de sesión fallido.",
    network_error:          "Error de red — verifique su conexión.",

    /* ── Shared ── */
    back:                   "Regresar",
    back_to_hub:            "Inicio",
    loading:                "Cargando…",
    retry:                  "Reintentar",
    search:                 "Buscar",
    confirm:                "Confirmar",
    cancel:                 "Cancelar",
    save:                   "Guardar",
    add:                    "Agregar",
    go:                     "Ir",
    today:                  "HOY",
    history:                "Historial",
    refresh:                "Actualizar",
    scan:                   "Escanear",
    clear:                  "Limpiar",
    units:                  "unidades",
    open_camera:            "Escanear Estante",
    or_enter_manually:      "O ingresar código manualmente",
    type_instead:           "⌨ Escribir (código dañado o difícil)",
    hide_manual:            "Ocultar entrada manual",
    looking_up:             "Buscando…",
    try_again:              "Intentar de nuevo",
    scan_another:           "Escanear otro",
    redo:                   "✎ Reescanear",

    /* ── Outbound ── */
    outbound_title:         "Salida",
    outbound_sub:           "Escanee pedido para iniciar",
    scan_invoice:           "Escanear Pedido",
    enter_invoice:          "Ingresar Pedido",
    camera_scan:            "Escanear con Cámara",
    scan_invoice_barcode:   "Escanear código de pedido",
    invoice_not_found:      "Pedido no encontrado.",
    invoice_placeholder2:   "ej. INV-2024-001",

    /* ── Check Stock ── */
    find_cabinet_title:     "Ver Stock",
    find_cabinet_sub:       "Escanee código de gabinete o estante para ver ubicaciones",
    cabinet_barcode_hint:   "Código de gabinete (ej. 8001-B15) o estante (ej. A-01)",
    barcode_placeholder:    "8001-B15 o A-01",
    looking_up_shelf:       "Buscando…",
    lookup_failed:          "Búsqueda fallida. Intente de nuevo.",
    cabinet_label:          "Gabinete",
    shelf_label:            "Ubicación en Estante",
    no_locations_found:     "No se encontraron ubicaciones para este gabinete.",
    found_in_locations:     "Encontrado en",
    location_s:             "ubicación(es)",
    shelf_empty:            "Este estante está vacío.",
    cabinet_types_on_shelf: "tipo(s) de gabinete en este estante",

    /* ── Move Cabinet ── */
    move_cabinet_title:     "Mover Gabinete",
    move_cabinet_sub:       "Estante origen → seleccionar → estante destino",
    step_from_shelf:        "Estante Origen",
    step_select_items:      "Seleccionar",
    step_to_shelf:          "Estante Destino",
    step_confirm_move:      "Confirmar",
    move_complete:          "Movimiento Completo",
    move_more:              "Mover Más",
    step1_scan_from:        "Paso 1: Escanear estante ORIGEN",
    step3_scan_to:          "Paso 3: Escanear estante DESTINO",
    loading_shelf:          "Cargando estante…",
    checking:               "Verificando…",
    shelf_not_found:        "Estante no encontrado. Escanee etiqueta (ej. A-01).",
    no_stock_on_shelf:      "No hay existencias en este estante.",
    could_not_read_shelf:   "No se pudo leer el estante. Intente de nuevo.",
    from_to_same:           "El estante origen y destino no pueden ser iguales.",
    shelf_not_found_to:     "Estante destino no encontrado. Escanee etiqueta (ej. B-02).",
    item_types_at_shelf:    "tipo(s) de artículo en este estante",
    deselect_all:           "Deseleccionar Todo",
    select_all:             "Seleccionar Todo",
    available:              "disponibles",
    max:                    "máx",
    select_at_least_one:    "Seleccione al menos un artículo para mover.",
    no_valid_items:         "No hay artículos válidos para mover.",
    next_scan_to:           "Siguiente → Escanear Estante Destino",
    selected:               "seleccionados",
    items_selected_to_move: "tipo(s) seleccionado(s) para mover",
    items_to_move:          "Artículos a Mover",
    confirm_move:           "Confirmar Movimiento",
    confirm_move_title:     "¿Confirmar este movimiento?",
    move_types_label:            "tipos",
    moving:                 "Moviendo…",
    move_failed:            "Movimiento fallido. Intente de nuevo.",
    item_types_moved:       "tipo(s) de artículo movido(s)",
    change_to_shelf:        "← Cambiar Estante Destino",
    back_to_selection:      "← Volver a Selección",
    /* división multi-ubicación */
    placed_so_far:          "Colocado hasta ahora:",
    placed_at:              "Colocado en:",
    from_location:          "Origen:",
    qty_max:                "máx",
    remaining_next:         "restantes → escanear siguiente estante",
    place_here_partial:     "Colocar aquí → escanear siguiente estante",
    qty_left:               "restantes",
    /* panel review paso 3 */
    review_hide:            "▲ Ocultar",
    review_edit_qty:        "▼ Ver / Editar Cantidad",
    /* modo SKU */
    sku_mode_shelf_err:     "Ese es un código de estante. Escanee un código de gabinete/SKU.",
    sku_mode_placeholder:   "Escanear o escribir SKU (ej. 8001-B15)",
    sku_mode_change:        "Cambiar SKU",
    sku_mode_select_from:   "Seleccionar estante origen",
    sku_mode_current_stock: "Stock actual:",
    sku_mode_searching:     "Buscando ubicaciones del SKU…",
    sku_mode_not_found:     "No se encontraron ubicaciones para este SKU.",
    /* coincidencia automática sufijo -D */
    d_trying_alt:           "Probando SKU alternativo…",
    d_matched_with:         "Coincidencia encontrada como",
    /* etiquetas de tarjeta paso 0 */
    mv_card_shelf_title:    "Escanear Ubicación",
    mv_card_shelf_sub:      "Escanee código del estante para ver todos los SKUs en él",
    mv_card_sku_title:      "Escanear Código SKU",
    mv_card_sku_sub:        "Escanee código del producto para encontrar los estantes que lo tienen",
    mv_from_shelf_placeholder: "Estante origen (ej. A-01)",

    /* ── Stock Adjustment ── */
    stock_adj_title:        "Ajuste de Inventario",
    stock_adj_sub:          "Escanear estante · ver existencias · ajustar cantidades",
    card_shelf_title:        "Escanear Ubicación",
    card_shelf_sub:         "Escanee el código de la ubicación para ver y ajustar todo el inventario en esa ubicación",
    card_sku_title:         "Escanear Gabinete",
    card_sku_sub:           "Escanee el código del gabinete para encontrar todas las ubicaciones donde se almacena",
    step1_scan_shelf:       "Paso 1: Escanear código de estante (ej. A-01)",
    open_camera_scan_shelf: "Abrir Cámara para Escanear Estante",
    looking_up_shelf2:      "Buscando estante...",
    shelf_not_found2:       "Estante no encontrado. Escanee etiqueta (ej. A-01).",
    could_not_read:         "No se pudo leer el estante. Intente de nuevo.",
    loading_shelf_inv:      "Cargando inventario del estante...",
    no_stock_on_shelf2:     "Sin existencias en este estante — agregue SKUs abajo.",
    was:                    "Antes:",
    add_new_sku:            "Agregar Nuevo SKU a Este Estante",
    scan_sku_barcode:       "Escanear Código SKU",
    or_enter_sku:           "O ingresar SKU manualmente",
    shelf_barcode_reject:   "Ese es un código de estante — escanee un gabinete/SKU.",
    sku_already_in_list:    "ya está en la lista.",
    no_changes:             "Sin cambios aún",
    submit_changes:         "Enviar",
    change_s:               "cambio(s)",
    saving:                 "Guardando...",
    no_changes_to_submit:   "Sin cambios para enviar — ajuste al menos una cantidad.",
    adjustment_saved:       "Ajuste Guardado",
    skus_updated:           "SKU(s) actualizado(s)",
    adjust_another:         "Ajustar Otro Estante",
    adj_failed:             "Error al enviar ajustes.",
    color_code_label:       "Código de Color",
    color_placeholder:      "Código de color ej. B12",
    color_variants:         "variante(s) de color — seleccione o escriba uno:",
    new_sku_enter_color:    "SKU nuevo — ingrese el Código de Color:",
    empty_barcode:          "Código vacío.",

    /* ── Return Stock ── */
    return_stock_title:     "Devolver Mercancía",
    return_stock_sub:       "Regresar artículos al estante",
    /* step bar */
    ret_step_cabinet:       "Gabinete",
    ret_step_location_qty:  "Ubicación & Cant.",
    ret_step_details:       "Detalles",
    ret_step_confirm:       "Confirmar",
    /* step 0 — scan SKU */
    ret_scan_sku_prompt:    "Escanear o ingresar código de gabinete",
    ret_scan_sku_hint:      "Ej. 8001-B15 o GREY-W3060",
    ret_scan_sku_placeholder: "Escanear o escribir código",
    ret_looking_up_sku:     "Buscando…",
    ret_shelf_barcode_err:  "Ese es un código de estante — escanee un gabinete.",
    ret_sku_lookup_fail:    "No se pudo encontrar el SKU. Intente de nuevo.",
    /* step 1 — pick location */
    ret_sku_badge:          "Gabinete SKU",
    ret_return_to_section:  "Devolver a ubicación",
    ret_no_existing_loc:    "Sin ubicación registrada para este SKU — escanee un estante abajo.",
    ret_current_stock:      "Stock actual:",
    ret_choose_another:     "Elegir otro estante",
    ret_scan_diff_shelf:    "Escanear otro código de estante",
    ret_shelf_not_found:    "Estante no encontrado. Escanee un código válido.",
    ret_shelf_lookup_fail:  "No se pudo leer el estante. Intente de nuevo.",
    ret_qty_to_return:      "Cantidad a devolver →",
    ret_next_details:       "Siguiente — Agregar Detalles →",
    /* step 2 — details */
    ret_return_to_badge:    "Devolver a",
    ret_invoice_label:      "Factura",
    ret_optional:           "(opcional)",
    ret_invoice_placeholder: "Ej. 274545-A",
    ret_note_label:         "Nota",
    ret_note_placeholder:   "Ej. Devolución cliente, color incorrecto",
    ret_review_confirm:     "Revisar y Confirmar →",
    ret_back:               "← Regresar",
    /* step 3 — confirm */
    ret_confirm_title:      "Confirmar Devolución",
    ret_col_sku:            "SKU",
    ret_col_return_to:      "Devolver a",
    ret_col_quantity:       "Cantidad",
    ret_col_invoice:        "Factura",
    ret_col_note:           "Nota",
    ret_confirm_btn:        "Confirmar Devolución ✓",
    ret_returning:          "Devolviendo…",
    /* errors */
    ret_no_shelf_err:       "Sin estante destino seleccionado.",
    ret_qty_err:            "La cantidad debe ser al menos 1.",
    ret_resolve_err:        "No se pudo resolver la ubicación. Vuelva a seleccionarla.",
    ret_failed:             "Error al devolver. Intente de nuevo.",
    /* done page */
    ret_done_title:         "Mercancía Devuelta",
    ret_done_sku:           "SKU",
    ret_done_location:      "Ubicación",
    ret_done_qty:           "Cant.",
    ret_done_invoice:       "Factura",
    ret_done_note:          "Nota",
    ret_return_another:     "Devolver Más",
    /* legacy keys kept for safety */
    step_shelf:             "Estante",
    step_cabinet:           "Gabinete",
    step_confirm_return:    "Confirmar",
    scan_shelf_label:       "Escanear código de estante (ej. A-01)",
    scan_cabinet_label:     "Escanear código de gabinete",
    or_enter_cab_manually:  "O ingresar código de gabinete manualmente",
    shelf_barcode_reject2:  "Ese es un código de estante. Escanee el gabinete.",
    valid_qty_error:        "Ingrese una cantidad válida.",
    qty_to_return:          "Cantidad a Devolver",
    enter_quantity:         "Ingrese cantidad",
    confirm_return:         "Confirmar Devolución",
    returning:              "Devolviendo...",
    return_failed:          "Error al devolver existencias. Intente de nuevo.",
    stock_returned:         "Existencias Devueltas",
    return_more:            "Devolver Más",

    /* ── Picking — ScanSession auto-save / exit confirm ── */
    pick_resume_title:      "¿Continuar última sesión?",
    pick_resume_invoice:    "Pedido:",
    pick_resume_types:      "tipo(s) escaneados",
    pick_resume_units:      "unidades",
    pick_saved_min_ago:     "min atrás",
    pick_saved_recently:    "Guardado recientemente",
    pick_resume_btn:        "Continuar",
    pick_discard_btn:       "Descartar",
    pick_exit_title:        "¿Salir de esta sesión?",
    pick_exit_body:         "tipo(s) escaneados guardados. Puede continuar la próxima vez que abra Surtido.",
    pick_exit_for:          "para",
    pick_leave_btn:         "Salir",
    pick_stay_btn:          "Quedarse",

    /* ── Picking — OrderList ── */
    assembly_picking:       "Surtido de Pedidos",
    orders_needing_pick:    "Pedidos por surtir",
    no_orders_to_pick:      "No hay pedidos por surtir",
    start_new_scan:         "Iniciar Nuevo Escaneo",
    start_new_scan_sub:     "Escanear códigos de gabinete sin seleccionar pedido",
    overdue:                "VENCIDO",
    items_picked:           "piezas surtidas",

    /* ── Picking — ScanSession ── */
    picking_free_scan:      "Surtido — Escaneo Libre",
    items_scanned:          "tipo(s) escaneados",
    invoice:                "Pedido:",
    invoice_placeholder:    "Ingrese no. de pedido o escanee código INV",
    change:                 "Cambiar",
    scan_cabinet_barcode:   "Escanear Código de Gabinete",
    enter_search_barcode:   "Ingresar o buscar código",
    use_camera:             "Usar Cámara",
    scan_list:              "Lista de Escaneo",
    no_items_scanned:       "Aún no se han escaneado artículos",
    clear_all:              "Borrar Todo",
    pull_order:             "Confirmar Salida",
    total_units:            "unidad(es)",
    types:                  "tipo(s)",
    total_types:            "tipo(s)",
    invoice_set:            "Pedido asignado:",
    added:                  "Agregado:",
    qty_updated:            "Cantidad actualizada:",
    invoice_required:        "Se requiere número de pedido antes de enviar.",
    empty_nothing:          "Lista vacía. Nada que enviar.",
    failed_submit:          "Error al enviar. Por favor reintente.",
    step_enter_invoice:     "Paso 1 de 2 — Ingresar Pedido",
    enter_invoice_prompt:   "Ingrese Número de Pedido",
    enter_invoice_hint:     "Escanee el código o escríbalo abajo",
    start_scanning:         "Iniciar Escaneo",

    /* ── Picking — Done ── */
    pull_complete:          "Salida Completada",
    pull_submitted:         "Salida Registrada",
    start_new_scan_btn:     "Nuevo Escaneo",
    view_history:           "Ver Historial",
    recorded:               "registradas",

    /* ── Picking — History ── */
    picking_history_title:  "Historial de Surtido",
    records:                "registro(s)",
    date_placeholder:       "Fecha AAAA-MM-DD",
    invoice_no:             "No. de pedido",
    no_records:             "No se encontraron registros",
    failed_load_history:    "Error al cargar historial. Verifique conexión.",

    /* ── Edit modal ── */
    edit_scan_entry:        "Editar Entrada",
    barcode:                "Código:",
    item:                   "Artículo:",
    qty:                    "Cantidad:",
    note:                   "Nota:",
    note_optional:          "Nota opcional",

    /* ── Pull Order confirm ── */
    confirm_pull_order:     "Confirmar Salida de Pedido",
    note_optional_label:    "Nota (opcional):",
    add_note:               "Agregar nota…",

    /* ── Quick Pull ── */
    quick_pull_title:       "Salida Rápida",
    scan_any_cabinet:       "escanear cualquier gabinete",
    types_label:            "Tipos",
    cabinets_label:         "Gabinetes",
    scan_list_empty:        "Lista de escaneo vacía",
    scan_start_hint:        "Escanee cualquier código de gabinete para iniciar",
    done_btn:               "Listo",
    new_scan:               "Nuevo Escaneo",
    quick_pull_done:        "Salida Rápida Lista",
    quick_pull_recorded:    "Salida Rápida Registrada",
    confirm_quick_pull:     "Confirmar Salida Rápida",
    confirm_submit:         "Confirmar y Enviar",
    no_invoice_needed:      "Escanee gabinetes — no se necesita pedido",
    failed_try_again:       "Falló. Inténtelo de nuevo.",

    /* ── History page ── */
    hub:                    "← Inicio",
    select_date:            "Seleccionar fecha",
    invoice_number:         "Número de pedido",
    show_all:               "Mostrar todo",
    nothing_on:             "Nada el",
    try_different:          "Pruebe otra fecha o número de pedido",
    loading_history:        "Cargando historial…",
    total_records:          "registro(s) en total",
    failed_load_orders:     "Error al cargar pedidos. Verifique conexión.",
    scan_at_least_one:      "Escanee al menos un gabinete primero.",
  },
};

/* ── Context ── */
const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    return localStorage.getItem("wms_lang") || "en";
  });

  const setLang = (l) => {
    localStorage.setItem("wms_lang", l);
    setLangState(l);
  };

  const t = (key) => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS["en"];
    return dict[key] ?? TRANSLATIONS["en"][key] ?? key;
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used inside <LanguageProvider>");
  return ctx;
}

/** Language switcher button row */
export function LangSwitcher({ style }) {
  const { lang, setLang } = useLang();
  const options = [
    { code: "en", label: "EN" },
    { code: "zh", label: "中文" },
    { code: "es", label: "ES" },
  ];
  return (
    <div style={{ display: "flex", gap: 4, ...style }}>
      {options.map(o => (
        <button
          key={o.code}
          onClick={() => setLang(o.code)}
          style={{
            padding: "3px 9px",
            borderRadius: 14,
            border: "1.5px solid",
            borderColor: lang === o.code ? "#7eb3e8" : "rgba(255,255,255,0.2)",
            background: lang === o.code ? "#1d4ed8" : "transparent",
            color: lang === o.code ? "#fff" : "rgba(255,255,255,0.55)",
            fontSize: 12,
            fontWeight: lang === o.code ? 700 : 400,
            cursor: "pointer",
            lineHeight: 1.4,
            transition: "all 0.15s",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
